// src/components/RefundCoupon/RefundCoupon.js
import React, { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import { Plus, Trash2, ArrowLeft, Mail } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2'; // <-- Import SweetAlert2
import 'sweetalert2/dist/sweetalert2.min.css'; // <-- Import the styles
// import './RefundCoupon.css';

const API_URL = process.env.REACT_APP_API_URL;

// Reusable fetch utility (no changes needed)
const fetchWithAuth = async (url, options = {}) => {
  const token = localStorage.getItem('adminToken');
  const headers = { 'Content-Type': 'application/json', ...options.headers };
  if (token) headers.Authorization = `Bearer ${token}`;
  const response = await fetch(url, { ...options, headers });
  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || 'An API error occurred');
  }
  return response.json();
};

const formatCurrency = (amount) => `₹${Number(amount).toFixed(2)}`;
const formatDate = (dateString) => new Date(dateString).toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' });

// --- Modal for Creating Refund Coupons ---
const RefundCouponModal = ({ isOpen, onClose, onSave }) => {
    const [users, setUsers] = useState([]);
    const [selectedUser, setSelectedUser] = useState(null); 
    const [description, setDescription] = useState('');
    const [amount, setAmount] = useState(100);
    const [usageLimit, setUsageLimit] = useState(1);

    useEffect(() => {
        if (isOpen) {
            fetchWithAuth(`${API_URL}/api/refund-coupons/helpers/users`)
                .then(setUsers)
                .catch(err => {
                    // MODIFIED: Replaced alert with Swal
                    Swal.fire({
                        icon: 'error',
                        title: 'Failed to Load Users',
                        text: err.message || 'There was a problem fetching the user list.',
                    });
                });
        } else {
            setSelectedUser(null);
            setDescription('');
            setAmount(100);
            setUsageLimit(1);
        }
    }, [isOpen]);

    const handleSave = (e) => {
        e.preventDefault();
        if (!selectedUser || !description || amount <= 0 || usageLimit < 1) {
            // MODIFIED: Replaced alert with Swal
            Swal.fire({
                icon: 'warning',
                title: 'Invalid Information',
                text: 'Please ensure all fields are filled out with valid values before saving.',
            });
            return;
        }
        onSave({ 
            userId: selectedUser._id, 
            firstName: selectedUser.firstName,
            lastName: selectedUser.lastName,
            description, 
            amount,
            usageLimit 
        });
    };

    if (!isOpen) return null;

    // ... The rest of the modal JSX is unchanged ...
    return (
        <div className="c-coupon-modal-overlay">
            <div className="c-coupon-modal-content">
                <form onSubmit={handleSave}>
                    <div className="c-coupon-modal-header"><h3>Create New Refund Coupon</h3></div>
                    <div className="c-coupon-form">
                         <div className="c-coupon-form-group full-width">
                            <label htmlFor="user-select">1. Select User</label>
                            <select id="user-select" value={selectedUser?._id || ''} onChange={e => setSelectedUser(users.find(u => u._id === e.target.value))} required>
                                <option value="" disabled>-- Choose a user --</option>
                                {users.map(u => <option key={u._id} value={u._id}>{u.firstName} {u.lastName} ({u.email})</option>)}
                            </select>
                        </div>
                        <div className="c-coupon-form-group full-width">
                             <label htmlFor="description">2. Reason for Refund (Description)</label>
                             <input type="text" id="description" value={description} onChange={e => setDescription(e.target.value)} placeholder="e.g., Refund for service cancellation" required />
                        </div>
                        <div className="c-coupon-form-group full-width">
                             <label htmlFor="amount">3. Refund Amount (₹)</label>
                             <input type="number" id="amount" value={amount} onChange={e => setAmount(Number(e.target.value))} min="1" required />
                        </div>
                        <div className="c-coupon-form-group full-width">
                             <label htmlFor="usageLimit">4. Usage Limit</label>
                             <input type="number" id="usageLimit" value={usageLimit} onChange={e => setUsageLimit(parseInt(e.target.value, 10) || 1)} min="1" required />
                        </div>
                    </div>
                    <div className="c-coupon-modal-footer">
                        <button type="button" className="c-coupon-btn c-coupon-btn-secondary" onClick={onClose}>Cancel</button>
                        <button type="submit" className="c-coupon-btn c-coupon-btn-primary">Generate and Save Coupon</button>
                    </div>
                </form>
            </div>
        </div>
    );
};


// --- MAIN REFUND COUPON PAGE COMPONENT ---
const RefundCoupon = () => {
    const [refundCoupons, setRefundCoupons] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [sendingEmailId, setSendingEmailId] = useState(null);
    const navigate = useNavigate();

    const fetchRefundCoupons = useCallback(async () => {
        setLoading(true);
        try {
            const data = await fetchWithAuth(`${API_URL}/api/refund-coupons`);
            setRefundCoupons(data);
        } catch (err) { setError(err.message); } 
        finally { setLoading(false); }
    }, []);

    useEffect(() => { fetchRefundCoupons(); }, [fetchRefundCoupons]);
    
    const handleSave = async (couponData) => {
        try {
            const result = await fetchWithAuth(`${API_URL}/api/refund-coupons`, {
                method: 'POST',
                body: JSON.stringify(couponData),
            });
            // MODIFIED: Replaced alert with Swal
            Swal.fire({
                icon: 'success',
                title: 'Coupon Created!',
                html: `A new coupon has been generated: <br><strong style="font-size: 1.2rem; margin-top: 8px; display: inline-block;">${result.coupon.code}</strong>`,
            });
            setIsModalOpen(false);
            fetchRefundCoupons();
        } catch (err) {
            // MODIFIED: Replaced alert with Swal
            Swal.fire({
                icon: 'error',
                title: 'Creation Failed',
                text: err.message || 'An error occurred while creating the coupon.',
            });
        }
    };
    
    const handleDelete = (couponId) => {
        // MODIFIED: Replaced window.confirm with Swal
        Swal.fire({
            title: 'Are you sure?',
            text: "This will permanently delete this refund coupon. You cannot undo this.",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Yes, delete it!'
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    const deleteResult = await fetchWithAuth(`${API_URL}/api/refund-coupons/${couponId}`, { method: 'DELETE' });
                    Swal.fire(
                        'Deleted!',
                        deleteResult.message || 'The coupon has been deleted.',
                        'success'
                    );
                    fetchRefundCoupons();
                } catch (err) {
                    Swal.fire(
                        'Failed!',
                        err.message || 'The coupon could not be deleted.',
                        'error'
                    );
                }
            }
        });
    };

    const handleSendEmail = (couponId) => {
        if (sendingEmailId) return;
        // MODIFIED: Replaced window.confirm with Swal
        Swal.fire({
            title: 'Send Coupon Email?',
            text: "This will send the refund coupon details to the user's registered email.",
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#aaa',
            confirmButtonText: 'Yes, send it!'
        }).then(async (result) => {
            if (result.isConfirmed) {
                setSendingEmailId(couponId);
                try {
                    const emailResult = await fetchWithAuth(`${API_URL}/api/refund-coupons/send-email`, {
                        method: 'POST',
                        body: JSON.stringify({ couponId }),
                    });
                    Swal.fire({
                        icon: 'success',
                        title: 'Email Sent!',
                        text: emailResult.message || 'The coupon email has been sent successfully.',
                    });
                } catch (err) {
                    Swal.fire({
                        icon: 'error',
                        title: 'Email Failed',
                        text: err.message || 'There was a problem sending the email.',
                    });
                } finally {
                    setSendingEmailId(null);
                }
            }
        });
    };
    
    // ... The rest of the component JSX is unchanged ...
    return (
        <div className="c-coupon-page">
             <Helmet>
                <title>Manage Refund Coupons | GN VISA Admin</title>
                <meta name="description" content="Create, manage, and send one-time refund coupons for specific users." />
            </Helmet>
            <div className="c-coupon-header">
                <div style={{display: 'flex', alignItems: 'center', gap: '1rem'}}>
                     <button className="c-coupon-btn-icon" onClick={() => navigate(-1)}><ArrowLeft size={20}/></button>
                     <h2>Refund Coupons</h2>
                </div>
                <button className="c-coupon-btn c-coupon-btn-primary" onClick={() => setIsModalOpen(true)}>
                    <Plus size={18} /> Create Refund Coupon
                </button>
            </div>
            {loading && <p>Loading...</p>}
            {error && <p style={{ color: 'red' }}>Error: {error}</p>}
               {!loading && !error && (
                <div className="c-coupon-table-wrapper">
                    <table className="c-coupon-table">
                        <thead>
                            <tr><th>Code</th><th>User</th><th>Description</th><th>Amount</th><th>Status</th><th>Usage</th><th>Expiry Date</th><th>Actions</th></tr>
                        </thead>
                        <tbody>
                            {refundCoupons.length > 0 ? (refundCoupons.map(c => {
                                 const isExpired = new Date(c.validUntil) < new Date();
                                 const isFullyUsed = c.usageCount >= c.usageLimit;
                                 const statusText = isFullyUsed ? "Used" : !c.isActive ? "Inactive" : isExpired ? "Expired" : "Active";
                                 const statusClass = isFullyUsed || !c.isActive || isExpired ? "c-coupon-status-inactive" : "c-coupon-status-active";
                                return (
                                <tr key={c._id}>
                                    <td><span className="c-coupon-code">{c.code}</span></td>
                                    <td>{c.user ? `${c.user.firstName} ${c.user.lastName}` : 'N/A'}</td>
                                    <td>{c.description}</td>
                                    <td>{formatCurrency(c.amount)}</td>
                                    <td><span className={`c-coupon-status-badge ${statusClass}`}>{statusText}</span></td>
                                    <td>{c.usageCount} / {c.usageLimit}</td>
                                    <td>{formatDate(c.validUntil)}</td>
                                    <td className="c-coupon-actions">
                                        <button 
                                            className="c-coupon-btn-icon"
                                            onClick={() => handleSendEmail(c._id)}
                                            disabled={sendingEmailId === c._id || isFullyUsed || !c.isActive}
                                            title={isFullyUsed ? 'Cannot send, coupon is fully used' : !c.isActive ? 'Cannot send, coupon is inactive' : 'Send coupon via email'}
                                        >
                                            {sendingEmailId === c._id ? '...' : <Mail size={16} />}
                                        </button>
                                        <button 
                                            className="c-coupon-btn c-coupon-btn-danger" 
                                            onClick={() => handleDelete(c._id)}
                                            disabled={sendingEmailId === c._id}
                                        >
                                            <Trash2 size={16} />
                                        </button>
                                    </td>
                                </tr>
                            )})) : (
                                <tr><td colSpan="8" style={{ textAlign: 'center', padding: '2rem' }}>No refund coupons found.</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            )}
            <RefundCouponModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} onSave={handleSave} />
        </div>
    );
};

export default RefundCoupon;